import { useCallback, type ReactNode } from 'react';

import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The tenant's MEDIA.
 *
 * One count per asset status, and the "needs attention" total — the assets a merchant has to
 * deal with: a failed scan, an upload that expired, or a file that was quarantined. The
 * statuses are the API's own vocabulary; the dashboard does not rename them.
 */
const STATUS_LABEL: Readonly<Record<string, string>> = {
  awaiting_upload: 'Awaiting upload',
  scan_pending: 'Awaiting scan',
  available: 'Available',
  quarantined: 'Quarantined',
  scan_failed: 'Scan failed',
  upload_expired: 'Upload expired',
};

export function MediaSummaryWidget({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => dashboardApi.media(), []);

  return (
    <DashboardWidgetFrame
      title="Media"
      load={load}
      className={className}
      isEmpty={(data) => data.total === 0}
      emptyTitle="No media yet"
      emptyDescription="Uploads land in the object store; the API settles what it finds there."
    >
      {(data) => (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <StatCard
              label="Assets"
              value={String(data.total)}
              caption="Every asset this tenant owns"
            />
            <StatCard
              label="Needs attention"
              value={String(data.needsAttention)}
              tone={data.needsAttention === 0 ? 'positive' : 'warning'}
              caption="Failed scans, expired uploads and quarantined files"
            />
          </div>

          <ul className="flex flex-col">
            {Object.entries(data.byStatus).map(([status, count]) => (
              <li
                key={status}
                className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0"
              >
                <span className="text-content">{STATUS_LABEL[status] ?? status}</span>
                <span className="font-medium text-content">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashboardWidgetFrame>
  );
}
